/**
 * Games API Service
 */

import http from '../client';
import { ENDPOINTS } from '../config';

/**
 * Save game data
 * @param {string} userId - User ID
 * @param {string} gameName - Game name (arrow, puzzle, memory, etc.)
 * @param {Object} gameData - Game data
 * @returns {Promise<Object>}
 */
export const saveGameData = async (userId, gameName, gameData) => {
  try {
    const response = await http.post(ENDPOINTS.GAMES.SAVE, {
      userId,
      gameName,
      gameData,
      timestamp: new Date().toISOString()
    });
    return response;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Get game leaderboard
 * @param {Object} params - Query parameters
 * @param {string} params.gameName - Filter by game
 * @param {number} params.limit - Number of entries
 * @param {string} params.period - Time period (daily, weekly, monthly, all)
 * @returns {Promise<Object>}
 */
export const getLeaderboard = async (params = {}) => {
  try {
    const response = await http.get(ENDPOINTS.GAMES.LEADERBOARD, params, { skipAuth: true });
    return response;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Get user's game scores
 * @param {string} userId
 * @param {Object} params - Query parameters
 * @param {string} params.gameName - Filter by game
 * @returns {Promise<Object>}
 */
export const getUserScores = async (userId, params = {}) => {
  try {
    const response = await http.get(ENDPOINTS.GAMES.USER_SCORES(userId), params);
    return response;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Prepare arrow game data for saving
 * @param {Object} gameResults
 * @returns {Object}
 */
export const prepareArrowGameData = (gameResults) => {
  return {
    correctCount: gameResults.correctCount || 0,
    wrongCount: gameResults.wrongCount || 0,
    accuracy: gameResults.accuracy || 0,
    avgTime: parseFloat(gameResults.avgTime) || 0,
    avgCorrectTime: parseFloat(gameResults.avgCorrectTime) || 0,
    totalScore: (gameResults.correctCount || 0) * 10,
    coins: gameResults.coins || 0,
    completedAt: new Date().toISOString()
  };
};

/**
 * Save arrow game results
 * @param {string} userId
 * @param {Object} gameResults
 * @returns {Promise<Object>}
 */
export const saveArrowGameResults = async (userId, gameResults) => {
  const gameData = prepareArrowGameData(gameResults);
  return await saveGameData(userId, 'arrow', gameData);
};

export default {
  saveGameData,
  getLeaderboard,
  getUserScores,
  prepareArrowGameData,
  saveArrowGameResults
};
